import { Suspense, useEffect, useMemo, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import Header from "../../Header";
import Sidebar from "../../Sidebar";

import useApi from "@/hooks/useApi";
import {
  useSettingsStore,
  useCompanyStore,
  useLoggedUserStore,
  useMyFaceStore,
} from "@/Store";

import S from "./styles.module.scss";
import { baseSidebar } from "../../Sidebar/sidebarItems";
import { canAccessModuleRoute, moduleForPath } from "@/Router/moduleAccess";
import ImpersonationBanner from "@/Components/ImpersonationBanner";
import FacialEnrollmentBanner from "@/Components/FacialEnrollmentBanner";
import ConnectionErrorScreen from "@/Components/ConnectionErrorScreen";
import PageLoader from "@/Components/PageLoader";
import { readImpersonationContext } from "@/Utils/authSession";
import { todayInputValue } from "@/Utils";
import { punchPeriodQuery } from "@/types/ITimeClockPunch";

const Layout = () => {
  const { getRequest } = useApi();
  const location = useLocation();
  const settings = useSettingsStore((state) => state.settings);
  const setSettings = useSettingsStore((state) => state.setSettings);
  const currentCompanyId = useCompanyStore((state) => state.currentCompanyId);
  const loggedUser = useLoggedUserStore((state) => state.loggedUser);
  const setLoggedUser = useLoggedUserStore((state) => state.setLoggedUser);
  const setMyFacialStatus = useMyFaceStore((state) => state.setMyFacialStatus);
  const clearMyFacialStatus = useMyFaceStore(
    (state) => state.clearMyFacialStatus
  );
  const [loadedCompanyId, setLoadedCompanyId] = useState<
    number | null | undefined
  >(undefined);
  const [accessLoadFailed, setAccessLoadFailed] = useState(false);
  const [accessAttempt, setAccessAttempt] = useState(0);
  const impersonation = readImpersonationContext();

  useEffect(() => {
    let active = true;

    clearMyFacialStatus();

    if (currentCompanyId === null) {
      setLoadedCompanyId(null);
      setAccessLoadFailed(false);
      return () => {
        active = false;
      };
    }

    setAccessLoadFailed(false);

    const loadFacialStatus = async (isCurrent: () => boolean) => {
      try {
        const today = todayInputValue();
        const sheetResponse = await getRequest(
          `/api/time-clock-punch/me?${punchPeriodQuery(today, today)}`
        );
        const sheetData = await sheetResponse.json();
        const employeeId = sheetData?.response?.employee?.id;
        if (!employeeId) {
          if (isCurrent()) clearMyFacialStatus();
          return;
        }

        const facesResponse = await getRequest(
          `/api/employee-face/employee/${employeeId}`
        );
        const facesData = await facesResponse.json();
        if (isCurrent())
          setMyFacialStatus(employeeId, (facesData?.response ?? []).length > 0);
      } catch {
        if (isCurrent()) clearMyFacialStatus();
      }
    };

    const loadCompanyAccess = async () => {
      try {
        const [settingsResponse, userResponse] = await Promise.all([
          getRequest("/api/settings"),
          getRequest("/api/user"),
        ]);
        const [settingsData, user] = await Promise.all([
          settingsResponse.json(),
          userResponse.json(),
        ]);
        if (!active) return;
        if (settingsData?.response) setSettings(settingsData.response);
        setLoggedUser(user);
        setLoadedCompanyId(currentCompanyId);

        if (settingsData?.response?.modules?.timeclock !== false)
          void loadFacialStatus(() => active);
      } catch {
        if (active) setAccessLoadFailed(true);
      }
    };
    loadCompanyAccess();
    return () => {
      active = false;
    };
  }, [currentCompanyId, accessAttempt]);

  const sidebar = useMemo(() => {
    const modules = settings?.modules;
    if (!modules) return baseSidebar;
    return baseSidebar.filter(
      (item) => !item.module || modules[item.module] !== false
    );
  }, [settings]);

  const currentModule = moduleForPath(location.pathname);
  const accessReady = loadedCompanyId === currentCompanyId;
  const blocked =
    accessReady &&
    !canAccessModuleRoute({
      module: currentModule,
      userType: loggedUser?.type,
      userModules: loggedUser?.modules ?? [],
      companyModules: settings?.modules,
    });

  return (
    <div
      className={`${S["grid-container"]} ${
        impersonation ? S.gridImpersonating : ""
      }`}
    >
      {impersonation && <ImpersonationBanner context={impersonation} />}
      <FacialEnrollmentBanner />
      <Header sidebarData={sidebar} />
      <Sidebar sidebarData={sidebar} />
      {!accessReady ? (
        accessLoadFailed ? (
          <ConnectionErrorScreen
            description="Não conseguimos carregar os acessos da empresa. Confira sua internet e tente de novo em instantes."
            onRetry={() => setAccessAttempt((n) => n + 1)}
          />
        ) : (
          <PageLoader fullscreen={false} />
        )
      ) : blocked ? (
        <Navigate to="/visao-geral" replace />
      ) : (
        <Suspense fallback={<PageLoader fullscreen={false} />}>
          <Outlet key={currentCompanyId ?? "no-company"} />
        </Suspense>
      )}
    </div>
  );
};

export default Layout;
